import { useId, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ApiError } from "../auth/AuthContext";
import { useAuth } from "../auth/useAuth";
import { erroreSulCampo } from "../api/client";
import { RequisitiPassword } from "../components/RequisitiPassword";
import { passwordValida } from "../lib/passwordRules";
import { useIsMobile } from "../hooks/useIsMobile";
import { useSiteConfig, resolveImageUrl } from "../config/SiteConfigContext";
import { PrivacyNotice } from "../components/PrivacyNotice";
import { ConsentCheckbox } from "../components/ConsentCheckbox";

export function Signup() {
  // Etichette collegate ai campi (accessibilità): id unici anche con più moduli in pagina.
  const fid = useId();
  const { locale } = useParams();
  const { t } = useTranslation();
  const { signup, resendVerification } = useAuth();
  const isMobile = useIsMobile();
  const cfg = useSiteConfig();
  const lang = locale || "it";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Consensi facoltativi e indipendenti: E) marketing diretto, F) profilazione.
  // Partono sempre da false e nessuno dei due condiziona l'altro o l'invio.
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [profilingConsent, setProfilingConsent] = useState(false);
  const [error, setError] = useState("");
  const [mostraRegole, setMostraRegole] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [resent, setResent] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    // Si controlla prima di partire: inutile far attendere una risposta del
    // server per dire una cosa che si sa già, e che l'elenco qui sotto sta
    // mostrando.
    if (!passwordValida(password)) {
      setError(t("auth.passwordNonValida"));
      setMostraRegole(true);
      return;
    }

    setSubmitting(true);
    try {
      // Si invia sempre la scelta reale, anche quando è false.
      await signup(email, password, { marketing: marketingConsent, profiling: profilingConsent }, lang);
      setSent(true);
    } catch (err) {
      // Mai la frase del server: è in inglese per tutti. Se è la password a
      // essere stata rifiutata, si mostra l'elenco dei requisiti tradotto.
      if (err instanceof ApiError && erroreSulCampo(err.detail, "password")) {
        setError(t("auth.passwordNonValida"));
        setMostraRegole(true);
      } else if (err instanceof ApiError && err.status === 409) {
        setError(t("auth.emailTaken"));
      } else if (err instanceof ApiError && err.status === 429) {
        setError(t("auth.tooManyAttempts"));
      } else {
        setError(t("common.error"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend() {
    setResent(false);
    try {
      await resendVerification(email, lang);
      setResent(true);
    } catch {
      setResent(true); // risposta sempre uguale (anti-enumeration): mostriamo comunque conferma
    }
  }

  const inputStyle: React.CSSProperties = {
    width: "100%",
    border: "1px solid rgba(28,75,59,0.89)",
    padding: "14px 18px",
    fontSize: 14,
    boxSizing: "border-box",
    borderRadius: 8,
    outline: "none",
    fontFamily: cfg.font_body,
    color: cfg.color_dark,
    background: cfg.color_body_bg,
    transition: "border-color 0.2s",
  };

  return (
    <div
      style={{
        minHeight: "80vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: isMobile ? "48px 20px" : "64px 24px",
        background: cfg.color_light_bg,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          background: cfg.color_body_bg,
          borderRadius: 8,
          padding: isMobile ? "36px 24px" : "48px 40px",
          border: "1px solid rgba(28,75,59,0.89)",
        }}
      >
        {/* Brand */}
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          {cfg.logo_url ? (
            <img
              src={resolveImageUrl(cfg.logo_url)}
              alt={cfg.brand_name}
              style={{ height: 36, objectFit: "contain", marginBottom: 16 }}
            />
          ) : (
            <div
              style={{
                fontSize: 20,
                fontWeight: 400,
                color: cfg.color_dark,
                fontFamily: cfg.font_heading,
                letterSpacing: 1,
                marginBottom: 16,
              }}
            >
              {cfg.brand_name}
            </div>
          )}
          <h1
            style={{
              fontSize: isMobile ? 22 : 26,
              fontWeight: 400,
              margin: 0,
              color: cfg.color_dark,
              fontFamily: cfg.font_heading,
            }}
          >
            {sent ? t("auth.checkEmailTitle") : t("auth.signupTitle")}
          </h1>
        </div>

        {sent ? (
          /* ── Schermata: controlla la tua email ── */
          <div style={{ textAlign: "center" }}>
            <p style={{ fontSize: 14, color: cfg.color_dark, lineHeight: 1.7, margin: "0 0 20px" }}>
              {t("auth.checkEmailBody", { email })}
            </p>
            {resent && (
              <p style={{ fontSize: 13, color: cfg.color_dark, background: cfg.color_light_bg, padding: "10px 14px", borderRadius: 8, margin: "0 0 16px" }}>
                {t("auth.resent")}
              </p>
            )}
            <button
              type="button"
              onClick={handleResend}
              style={{
                width: "100%",
                background: "transparent",
                color: cfg.color_dark,
                border: "1px solid rgba(28,75,59,0.89)",
                padding: "12px 24px",
                cursor: "pointer",
                fontWeight: 500,
                borderRadius: 8,
                fontSize: 13,
                letterSpacing: 1,
                textTransform: "uppercase",
                marginBottom: 12,
              }}
            >
              {t("auth.resend")}
            </button>
            <Link
              to={`/${lang}/login`}
              style={{ display: "block", color: cfg.color_muted, textDecoration: "none", fontSize: 14 }}
            >
              {t("auth.goToLogin")}
            </Link>
          </div>
        ) : (
          <>
            <form onSubmit={handleSubmit}>
              {error && (
                <p
                  role="alert"
                  style={{
                    color: "#991b1b",
                    marginBottom: 16,
                    fontSize: 13,
                    background: "#fef2f2",
                    padding: "10px 14px",
                    borderRadius: 8,
                  }}
                >
                  {error}
                </p>
              )}

              <div style={{ marginBottom: 18 }}>
                <label htmlFor={`${fid}-1`}
                  style={{
                    display: "block",
                    fontSize: 11,
                    fontWeight: 500,
                    letterSpacing: 1.5,
                    textTransform: "uppercase",
                    marginBottom: 8,
                    color: cfg.color_muted,
                  }}
                >
                  {t("auth.email")}
                </label>
                <input id={`${fid}-1`}
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: 24 }}>
                <label htmlFor={`${fid}-2`}
                  style={{
                    display: "block",
                    fontSize: 11,
                    fontWeight: 500,
                    letterSpacing: 1.5,
                    textTransform: "uppercase",
                    marginBottom: 8,
                    color: cfg.color_muted,
                  }}
                >
                  {t("auth.password")}
                </label>
                <input id={`${fid}-2`}
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setMostraRegole(true)}
                  aria-describedby="requisiti-password"
                  autoComplete="new-password"
                  style={inputStyle}
                />
                <div id="requisiti-password">
                  <RequisitiPassword password={password} mostraSempre={mostraRegole} />
                </div>
              </div>

              {/* GDPR: presa visione dell'informativa per le finalità B), C) e D) con
                  l'invio del form (nessuna checkbox), poi i due consensi facoltativi. */}
              <div style={{ marginBottom: 24, display: "flex", flexDirection: "column", gap: 16 }}>
                <PrivacyNotice variant="auth" />
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <ConsentCheckbox
                    name="marketing_consent"
                    label={t("consent.marketing")}
                    checked={marketingConsent}
                    onChange={setMarketingConsent}
                  />
                  <ConsentCheckbox
                    name="profiling_consent"
                    label={t("consent.profiling")}
                    checked={profilingConsent}
                    onChange={setProfilingConsent}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                style={{
                  width: "100%",
                  background: cfg.color_dark,
                  color: "#fff",
                  border: "none",
                  padding: "14px 24px",
                  cursor: submitting ? "default" : "pointer",
                  fontWeight: 500,
                  borderRadius: 8,
                  fontSize: 13,
                  letterSpacing: 1,
                  textTransform: "uppercase",
                  opacity: submitting ? 0.7 : 1,
                  transition: "opacity 0.2s",
                }}
                onMouseEnter={(e) => {
                  if (!submitting) e.currentTarget.style.opacity = "0.85";
                }}
                onMouseLeave={(e) => {
                  if (!submitting) e.currentTarget.style.opacity = "1";
                }}
              >
                {submitting ? "..." : t("auth.signupBtn")}
              </button>
            </form>

            <div
              style={{
                marginTop: 24,
                paddingTop: 24,
                borderTop: "1px solid rgba(28,75,59,0.89)",
                textAlign: "center",
              }}
            >
              <p style={{ fontSize: 14, color: "rgba(28,75,59,0.86)", margin: 0 }}>
                {t("auth.haveAccount")}{" "}
                <Link
                  to={`/${lang}/login`}
                  style={{ color: cfg.color_dark, textDecoration: "none", fontWeight: 500 }}
                >
                  {t("auth.loginLink")}
                </Link>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
